/**
 * REST 연결 정직성 (설계 지침 §7-2·§7-3) — 두 신호를 connection 스토어에 합류.
 *
 * 1) REST 폴링 건강 ("rest" 채널): react-query QueryCache 전역 onError/onSuccess
 *    (main.tsx) 가 reportRestError/reportRestSuccess 를 호출 — 연속 실패 횟수를
 *    채널 attempts 로 보고해, WS 채널이 0개인 REST 전용 화면(Command 등)에서도
 *    백엔드 다운이 ConnectionBanner 로 노출된다.
 *    (defaultOptions.queries.retry=1 → onError 1회 = 연속 요청 실패 ≥2회)
 *
 * 2) 오프라인 스냅샷 감지 ("rest-snapshot" 채널): SW NetworkFirst 캐시 폴백은
 *    HTTP 200 으로 반환되어 성공처럼 보이므로(vite.config.ts runtimeCaching),
 *    응답 Date 헤더 나이 > SNAPSHOT_MAX_AGE_MS 를 캐시 폴백으로 추정해 보고
 *    → 배너가 "오프라인 스냅샷 표시 중 — 마지막 데이터 n초/분 전" 표기.
 *    Date 헤더 부재·파싱 실패·비 GET 응답은 무동작 (오탐 금지).
 */
import { QueryCache } from "@tanstack/react-query";

import { removeChannel, reportChannel } from "@/store/connection";

/** react-query 전역 실패/회복을 보고하는 채널 id */
export const REST_CHANNEL_ID = "rest";
/** SW 캐시 폴백(오프라인 스냅샷) 감지를 보고하는 채널 id */
export const SNAPSHOT_CHANNEL_ID = "rest-snapshot";
/** 응답 Date 헤더 나이가 이보다 오래되면 SW 캐시 폴백(스냅샷)으로 추정 */
export const SNAPSHOT_MAX_AGE_MS = 15_000;

let consecutiveFailures = 0;
let lastSuccessAt: number | null = null;

/** QueryCache 전역 onError — 연속 실패를 "rest" 채널로 보고 */
export function reportRestError(): void {
  consecutiveFailures += 1;
  reportChannel(REST_CHANNEL_ID, {
    status: "closed",
    attempts: consecutiveFailures,
    lastMessageAt: lastSuccessAt,
    kind: "rest",
  });
}

/** QueryCache 전역 onSuccess — 실패 이력이 있을 때만 회복 보고 (스토어 churn 방지) */
export function reportRestSuccess(now: number = Date.now()): void {
  lastSuccessAt = now;
  if (consecutiveFailures === 0) return;
  consecutiveFailures = 0;
  reportChannel(REST_CHANNEL_ID, {
    status: "open",
    attempts: 0,
    lastMessageAt: now,
    kind: "rest",
  });
}

/** 테스트용 — 모듈 상태(연속 실패 카운터·마지막 성공 시각) 리셋 */
export function resetRestHealth(): void {
  consecutiveFailures = 0;
  lastSuccessAt = null;
}

/** main.tsx QueryClient 에 주입 — 모든 쿼리 성패를 connection 스토어로 합류 */
export function createRestHealthQueryCache(): QueryCache {
  return new QueryCache({
    onError: () => reportRestError(),
    onSuccess: () => reportRestSuccess(),
  });
}

/** axios 응답에서 필요한 최소 구조 (테스트에서 plain object 주입 가능) */
interface FreshnessCheckable {
  headers?: Record<string, unknown>;
  config?: { method?: string };
}

/**
 * 응답 Date 헤더 나이 검사 — client.ts 응답 인터셉터에서 호출.
 * SW runtimeCaching 은 GET /api/v1 만 캐시하므로 GET 외에는 검사하지 않는다.
 */
export function trackRestFreshness(response: FreshnessCheckable, now: number = Date.now()): void {
  const method = (response.config?.method ?? "get").toLowerCase();
  if (method !== "get") return;
  const raw = response.headers?.["date"];
  if (typeof raw !== "string" || raw === "") return; // Date 헤더 부재 — 무동작 (오탐 금지)
  const headerTime = Date.parse(raw);
  if (Number.isNaN(headerTime)) return; // 파싱 실패 — 무동작
  if (now - headerTime > SNAPSHOT_MAX_AGE_MS) {
    // 캐시 폴백 추정 — 스냅샷 채널 보고 (lastMessageAt = 캐시 데이터 생성 시각)
    reportChannel(SNAPSHOT_CHANNEL_ID, {
      status: "closed",
      attempts: 0,
      lastMessageAt: headerTime,
      kind: "rest-snapshot",
    });
  } else {
    // 신선한 네트워크 응답 — 스냅샷 상태 해제
    removeChannel(SNAPSHOT_CHANNEL_ID);
  }
}
